/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import api from "@/http/api";
import { useSocketStore } from "@/socketStore";
import { useAuthStore } from "@/store";
import { useMutation, useQuery, useInfiniteQuery, useQueryClient, keepPreviousData, type InfiniteData } from "@tanstack/react-query";
import { toast } from "sonner";

const getOrders = (search?: string, page: number = 1, limit: number = 50, status?: string, labels: string[] = [], submitted = false) => {
  const query = new URLSearchParams();
  if (search) query.append('search', search);
  if (submitted) query.append('submitted', '1');
  if (status) query.append('status', status);
  // Repeated params, matching the array the controller expects. Sorted so the
  // same selection made in a different order reuses one cache entry.
  for (const label of [...labels].sort()) query.append('labels', label);
  query.append('page', page.toString());
  query.append('limit', limit.toString());
  return api.get(`/order/all?${query.toString()}`);
};

export const useOrder = (search?: string, page: number = 1, limit: number = 50, options?: any, labels: string[] = [], submitted = false) => {
  const labelKey = [...labels].sort();
  return useQuery({
    queryKey: ["all-orders", search, page, limit, labelKey, submitted],
    queryFn: async () => {
      return await getOrders(search, page, limit, undefined, labels, submitted);
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

// `status` scopes the query to a single Kanban column so each column owns its
// own cursor. Without it every column shares one window over the whole order
// list and sparse statuses stay empty until you page past everything newer.
// "N orders submitted" above the board. Keyed under
// "all-orders" so the invalidations that refresh the board after a status
// change refresh this too.
export const useSubmittedCount = () => {
  return useQuery({
    queryKey: ["all-orders", "submitted-count"],
    queryFn: async () => {
      const res = await api.get(`/order/all/submitted-count`);
      return res.data.count as number;
    },
  });
};

// --- Notifications page --------------------------------------------------------

// Priority tab: orders that need the team (API: getPriority in order/attention.js).
export type PriorityRule = { id: string; label: string; urgent: boolean; handledLabel: string; since: string | null };
export type PriorityRow = {
  id: number;
  order_id: string;
  customer_id: number;
  status: string;
  job_title: string | null;
  c_name: string | null;
  c_business_name: string | null;
  rules: PriorityRule[];
  wait_since: string | null;
  claimed_by: number | null;
  claimed_by_name: string | null;
  claimed_at: string | null;
  last_message: { sender_role: string; sender_name: string | null; text: string; created_at: string } | null;
};

const PRIORITY_KEY = ["all-orders", "priority"];

// Under "all-orders" so board moves and live socket events refresh it; the
// interval covers time-based rules and other admins' claims.
export const usePriority = () => {
  return useQuery({
    queryKey: PRIORITY_KEY,
    queryFn: async () => {
      const res = await api.get(`/order/all/priority`);
      return res.data as { urgentCount: number; rows: PriorityRow[] };
    },
    refetchInterval: 60_000,
  });
};

// Other tab: this admin's notifications (API: getNotificationFeed).
export type FeedType = "messages" | "discussion" | "status" | "orders" | "cad" | "instructions";
export type FeedItem = {
  id: number;
  order_id: number;
  action_type: string;
  action_by: string;
  created_at: string;
  is_seen: number;
  order_ref: string;
  job_title: string | null;
  customer_id: number;
  c_name: string | null;
  c_business_name: string | null;
  actor_name: string | null;
  status: string | null;
  snippet: string | null;
};
type FeedPage = { rows: FeedItem[]; hasMore: boolean };

// The live socket handler in the layout invalidates ["notifications"], so new
// notifications show up here without polling.
export const useNotificationFeed = (type: FeedType | null, unreadOnly: boolean) => {
  return useInfiniteQuery({
    queryKey: ["notifications", "feed", type, unreadOnly],
    queryFn: async ({ pageParam }) => {
      const query = new URLSearchParams();
      if (pageParam) query.set("before", String(pageParam));
      if (type) query.set("type", type);
      if (unreadOnly) query.set("unread", "1");
      const res = await api.get(`/notification/feed?${query}`);
      return res.data as FeedPage;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (last: FeedPage) => (last.hasMore ? last.rows.at(-1)?.id ?? null : null),
    placeholderData: keepPreviousData,
  });
};

type NotificationAction =
  | { kind: "claim"; order_id: number; claim: boolean }
  // One request per open rule on the order; since null undoes.
  | { kind: "handled"; order_id: number; rules: { id: string; since: string | null }[] }
  | { kind: "read"; ids: number[] }
  | { kind: "read-all" };

const runNotificationAction = async (a: NotificationAction): Promise<void> => {
  switch (a.kind) {
    case "claim":
      await api.post(`/order/all/attention/${a.order_id}/claim`, { claim: a.claim });
      return;
    case "handled":
      await Promise.all(a.rules.map((r) => api.post(`/order/all/attention/${r.id}/${a.order_id}/handled`, { since: r.since })));
      return;
    case "read":
      await Promise.all(a.ids.map((id) => api.post(`/notification/${id}/mark-seen`)));
      return;
    case "read-all":
      await api.post(`/notification/mark-all-seen`);
      return;
  }
};

export const useNotificationAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runNotificationAction,
    onSettled: (_data, _err, a) => {
      if (a.kind === "claim" || a.kind === "handled") {
        // PRIORITY_KEY also drives the sidebar badge.
        queryClient.invalidateQueries({ queryKey: PRIORITY_KEY });
      } else {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    },
    onError: () => toast.error("Couldn't save that. Check your connection and try again."),
  });
};

export const useInfiniteOrder = (search?: string, limit: number = 50, status?: string, options?: any, labels: string[] = [], submitted = false) => {
  const labelKey = [...labels].sort();
  return useInfiniteQuery({
    queryKey: ["all-orders-infinite", search, limit, status, labelKey, submitted],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getOrders(search, pageParam as number, limit, status, labels, submitted);
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { currentPage, totalPages } = lastPage.pagination;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

const updateOrderStatus = (
  order_id: string,
  customer_id: string,
  status: string,
  orderRef: string
) => {
  console.log('status status',status)
  return api.post(`/order/update/${orderRef}/${customer_id}`, {
    status,
    orderRef,
  });
};

export const useOrderStatus = () => {
  const queryClient = useQueryClient()
  const sendNotification = useSocketStore((s) => s.sendNotification);
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: ({
      order_id,
      customer_id,
      status,
      orderRef,
    }: {
      order_id: string;
      customer_id: string;
      status: string;
      orderRef: string;
    }) => updateOrderStatus(order_id, customer_id, status, orderRef),
    onError: () => {
      toast.error("Something went wrong!");
    },
    onSuccess: ({ data }, variables) => {
      toast.success('Order Status changed')
      // "all-orders" does not prefix-match "all-orders-infinite" -- they are
      // separate root keys -- so the Kanban board needs its own line. Without
      // it a drag only lives in KanbanView's local state and snaps back the
      // moment any column loads a page.
      // ponytail: refetches every loaded page of every column. Fine at one
      // page per column; move to a targeted setQueryData on source/target
      // status if boards get deep.
      queryClient.invalidateQueries({ queryKey: ["all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["all-orders-infinite"] });
      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.id.toString()!,
        action_type: "status_updated",
        order_id: variables.orderRef,
        title: `Order status changed`,
        metadata: {
          order_ref: variables.order_id,
          order_id: variables.order_id,
          customer_id: data?.updatedOrder.customer_id.toString(),
          adminName: user?.full_name!,
          job_title: data.updatedOrder.job_title,
          status: variables.status,
        },
      });
    },
  });
};

const getOrderInst = (order_id: string) => {
  return api.get(`/admin/order/instruction/${order_id}`);
};

export const useOrderInst = (order_id: string) => {
  return useQuery({
    queryKey: ["order-instruction", order_id],
    queryFn: async () => {
      return await getOrderInst(order_id);
    },
    enabled: !!order_id,
  });
};

const updateOrder = (
  order_id: string,
  customer_id: string,
  updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string }
) => {
  return api.post(`/order/update/${updates.orderRef}/${customer_id}`, {
    ...updates,
    labels: updates.labels ? JSON.stringify(updates.labels) : undefined,
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      order_id,
      customer_id,
      updates,
    }: {
      order_id: string;
      customer_id: string;
      updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string };
    }) => updateOrder(order_id, customer_id, updates),
    onSuccess: () => {
      // "all-orders" does not prefix-match "all-orders-infinite" -- they are
      // separate root keys -- so the Kanban board needs its own line. Without
      // it a drag only lives in KanbanView's local state and snaps back the
      // moment any column loads a page.
      // ponytail: refetches every loaded page of every column. Fine at one
      // page per column; move to a targeted setQueryData on source/target
      // status if boards get deep.
      queryClient.invalidateQueries({ queryKey: ["all-orders"] });
      queryClient.invalidateQueries({ queryKey: ["all-orders-infinite"] });
      toast.success("Order updated successfully");
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });
};
